import { type PartnerTreeNodeV2 } from '@sr/types';

/**
 * Searches through a tree of partner nodes to find an item that matches the provided condition
 * Uses an iterative approach with a stack to avoid call stack issues
 *
 * @param partnerTree - Single node or array of partner tree nodes to search
 * @param predicate - Function that returns true when a matching item is found
 * @returns true if any node matches the condition, false otherwise
 */
export const partnerTreeSearch = (
  partnerTree: PartnerTreeNodeV2 | PartnerTreeNodeV2[],
  predicate: (item: PartnerTreeNodeV2['item']) => boolean,
): boolean => {
  // Initialize stack with input nodes
  const stack: PartnerTreeNodeV2[] = Array.isArray(partnerTree) ? [...partnerTree] : [partnerTree];

  while (stack.length > 0) {
    const current = stack.pop();

    // Skip null/undefined nodes
    if (!current) continue;

    // Check if current node matches the condition
    if (predicate(current.item)) {
      return true; // Found a match, stop searching
    }

    // Add children to the stack to process them next
    if (Array.isArray(current.children) && current.children.length > 0) {
      stack.push(...current.children);
    }
  }

  // No match found after checking all nodes
  return false;
};
